import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { useMutation, useQuery } from '@/utils/api';

export interface AIBudgetStatus {
  set: boolean;
  max_limit: number;
  current_usage: number;
  reset_duration: string;
  calendar_aligned: boolean;
  last_reset?: string;
}

interface UseAIBudgetStatusOptions {
  email?: string;
}

/**
 * Reads the per-tenant Bifrost spend cap and current usage. Returns
 * `null` when the tenant has been provisioned but no Customer Budget
 * has been configured yet (backend returns 404 with a discriminated
 * message; we differentiate it from a hard error here).
 */
export const useAIBudgetStatus = ({ email }: UseAIBudgetStatusOptions = {}) => {
  const axios = useAxios({
    email,
    doNotImpersonate: !!email,
  });

  const queryKey = useGetUserKey(getQueryKey.getAIBudget(), !!email, email);

  return useQuery<AIBudgetStatus | null>({
    queryKey,
    queryFn: async () => {
      try {
        const { data } = await axios.get<AIBudgetStatus>('/bifrost/budget');
        return data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 30_000,
    gcTime: 300_000,
    defaultErrorMessage: 'Failed to load AI spend',
  });
};

interface UseSetAIBudgetOptions {
  email?: string;
}

/**
 * Praetorian-only mutation that sets the per-tenant monthly spend cap.
 * Invalidates `useAIBudgetStatus` on success so the card re-renders
 * with the new max_limit (Bifrost echoes back current_usage unchanged).
 */
export const useSetAIBudget = ({ email }: UseSetAIBudgetOptions = {}) => {
  const axios = useAxios({
    email,
    doNotImpersonate: !!email,
  });

  const { invalidate } = useAIBudgetStatus({ email });

  return useMutation({
    defaultErrorMessage: 'Failed to update AI spend limit',
    getErrorFromResponse: true,
    mutationFn: async ({ maxLimit }: { maxLimit: number }) => {
      await axios.put('/bifrost/budget', { max_limit: maxLimit });
      invalidate();
      toast.success('AI spend limit updated');
    },
  });
};
